import { LitElement, html, nothing, PropertyValues, TemplateResult } from 'lit';
import { property, customElement, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { GzCheckboxWebComponent } from '../../gzCheckbox';
import { WEB_COMPONENT_GZ_INPUT_SEARCH } from '../../../webcomponents.constants';
import { GzInputSearchWebComponent } from '../../gzInput';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { ifDefined } from 'lit/directives/if-defined.js';
import { desanitizeObject } from '../../../utils/sanitization.utils';
import { emphasizeString } from '../../../utils/htmlElement.utils';
import { ComplexEnumListEvents, ComplexSuggestedItem, ComplexPinnedListItem, ErrorMessageDetails } from '../types';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { htmlEncode } from '../../../utils/escape.utils';
import { GzIconLabelWebComponent } from '../../gzIconLabel';
import { entityIconSet } from '../../../theme/entity-icons';
import { FontSize } from '../../gzIconButton/types';

/**
 * @summary Type here the description of this component
 * @tag gz-complex-enum-list
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-complex-enum-list')
export class GzComplexEnumListWebComponent extends LitElement {
  /**
   * Get only the filterable checkboxes from the list; we don't want to get the value or label from the 'All' checkbox.
   */
  public get checkboxesList() {
    if (!this.shadowRoot) {
      return [];
    }

    return this.filteredCheckboxes(this.shadowRoot.querySelectorAll('.is-filterable'));
  }

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  @property({ type: Array, attribute: true, reflect: true })
  suggestedItems: ComplexSuggestedItem[] = [];

  @property({ type: Array, attribute: true, reflect: true })
  pinnedItems: ComplexPinnedListItem[] = [];

  @property({ type: String, attribute: true, reflect: true })
  searchPlaceholderString = '';

  @property({ type: String, attribute: true, reflect: true })
  noSearchResultsString = '';

  @property({ type: Number, attribute: true, reflect: true })
  searchMaxLength;

  @property({ type: Boolean, attribute: true, reflect: true })
  isLoading = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  isError = false;

  @property({ type: Object, attribute: false })
  errorMessageDetails: ErrorMessageDetails = {} as ErrorMessageDetails;

  @property({ type: Array, attribute: false, reflect: false })
  ignoredFieldsForFilters: Array<string> = [];

  @query('#emptyTemplate')
  private readonly emptyTemplate!: HTMLElement;

  @query('#loadingTemplateComplexEnumList')
  private readonly loadingTemplate!: HTMLElement;

  @query(WEB_COMPONENT_GZ_INPUT_SEARCH)
  public searchInput!: GzInputSearchWebComponent;

  @query('.list--options')
  private listOptions: HTMLElement;

  /**
   *
   * @private
   */
  private oldSearchString = '';

  /**
   *
   * @private
   */
  private scrolledLocation = 0;

  /**
   * Only return children nodes that are GzCheckboxWebComponent
   */
  filteredCheckboxes(nodes): GzCheckboxWebComponent[] {
    return Array.from(nodes).filter(child => child instanceof GzCheckboxWebComponent) as GzCheckboxWebComponent[];
  }
  /**
   * The state for the icon set instance
   */
  @state()
  private iconSetInstance: TemplateResult;

  constructor() {
    super();
    this.iconSetInstance = entityIconSet;
  }

  render() {
    const hasSearchInput = this.hasSearchInput();
    return html`
      <div class="list ${hasSearchInput ? 'has-search' : ''}">
        <div class="list--search">${this.renderSearchInput()}</div>

        ${this.renderListOptions()}
      </div>
    `;
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    if (
      _changedProperties.has('suggestedItems') ||
      _changedProperties.has('isLoading') ||
      _changedProperties.has('isError')
    ) {
      this.scrollToLastSaved();
    }
    this.emitSuggestedItemsResultsEvent(this.suggestedItems);
  }

  private scrollToLastSaved() {
    this.updateComplete.then(() => {
      setTimeout(() => {
        this.listOptions.scrollTop = this.scrolledLocation;
      }, 50);
    });
  }

  private handleLoadMore() {
    let searchString = '';
    if (!this.noSearchPerformed()) {
      searchString = this.getSearchInputValue();
    }

    const event = new CustomEvent(ComplexEnumListEvents.COMPLEX_LOAD_MORE, {
      detail: {
        searchString,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  private getSearchInputValue() {
    if (!this.searchInput) {
      return '';
    }
    return this.searchInput.value.trim();
  }

  protected renderSearchInput() {
    return html`<gz-input-search
      placeholder="${this.searchPlaceholderString}"
      inputWidth="medium"
      .maxLength="${ifDefined(this.searchMaxLength)}"
      @change="${this.handleOptionsSearch}"
    ></gz-input-search>`;
  }

  protected renderListOptions() {
    return html`<div class="list--options" @scroll="${this.handleScroll}">
      ${this.renderLoadingTemplate()} ${this.renderSuggestedItemsList()} ${this.renderNoResultsTemplate()}
      ${this.renderErrorTemplate()}
    </div>`;
  }

  private handleScroll() {
    if (!this.listOptions) {
      return;
    }
    if (
      Math.ceil(this.listOptions.scrollTop + this.listOptions.clientHeight) === this.listOptions.scrollHeight &&
      this.listOptions.scrollTop !== 0
    ) {
      this.scrolledLocation = this.listOptions.scrollTop;
      this.handleLoadMore();
    }
  }

  protected renderNoResultsTemplate() {
    if (this.isLoading || this.isError || !isEmpty(this.suggestedItems) || (!isEmpty(this.pinnedItems) && this.noSearchPerformed())) {
      return nothing;
    }

    return html`<div id="emptyTemplate" class="list--no-results visible">${this.noSearchResultsString}</div>`;
  }

  private renderErrorTemplate() {
    return this.isError
      ? unsafeHTML(`<div class="list--error">
          ${this.errorMessageDetails.message}
        </div>`)
      : nothing;
  }

  protected renderLoadingTemplate() {
    return this.isLoading
      ? html` <div id="loadingTemplateComplexEnumList" class="loading-container">
          <gz-progress-spinner
            class="d-flex flex-row justify-center align-items-center"
            spinnerwidth="medium"
          ></gz-progress-spinner>
        </div>`
      : nothing;
  }

  protected renderSuggestedItemsList() {
    if (this.isLoading) {
      return nothing;
    }

    if (this.noSearchPerformed()) {
      return this.renderOptionsNoSearch();
    } else {
      return this.renderOptionsSearchIsActive();
    }
  }

  /**
   * If no search, show pinned items at the top of the list
   *
   * @private
   */
  private renderOptionsNoSearch() {
    const pinnedItemsHtml = html`${this.pinnedItems.map(option => {
      return this.renderPinnedItems(option);
    })}`;

    if (isEmpty(this.suggestedItems)) {
      return html` ${pinnedItemsHtml}`;
    }

    const suggestedItemsHtml = html`${this.suggestedItems.map(option => {
      return this.renderDefaultSuggestedItems(option);
    })}`;

    return html` ${pinnedItemsHtml} ${suggestedItemsHtml} `;
  }

  /**
   * Search filter applied, show only suggested items
   * @private
   */
  private renderOptionsSearchIsActive() {
    if (isEmpty(this.suggestedItems)) {
      return;
    }

    return html`${this.suggestedItems.map(option => this.renderDefaultSuggestedItems(option))}`;
  }

  /**
   *
   * @param option
   * @private
   */
  private checkItemExistsInPinnedItemsList(option: ComplexSuggestedItem) {
    return (
      this.pinnedItems.filter(item => {
        return item.value === option.value;
      }).length > 0
    );
  }

  /**
   *
   * @param option
   * @private
   */
  private checkItemExistsInSuggestedItemsList(option: ComplexSuggestedItem) {
    return (
      this.suggestedItems.filter(item => {
        return item.value === option.value;
      }).length > 0
    );
  }

  /**
   *
   * @param option
   * @private
   */
  private renderPinnedItems(option: ComplexPinnedListItem) {
    if (this.checkItemExistsInSuggestedItemsList(option)) {
      return nothing;
    }

    return this.renderListItem(option, option.checked);
  }

  /**
   *
   * @param option
   * @private
   */
  private renderDefaultSuggestedItems(option: ComplexSuggestedItem) {
    const [matchedItem] = this.pinnedItems.filter(item => {
      return item.value === option.value;
    });

    const checked = matchedItem && matchedItem.checked;

    return this.renderListItem(option, checked);
  }

  /**
   *
   * @param option
   * @param checkedValue
   * @private
   */
  private renderListItem(option: ComplexSuggestedItem, checkedValue) {
    option = desanitizeObject(option);
    const searchValue = this.getSearchInputValue();

    return html`
      <div class="option option-selectable">
        <gz-checkbox
          class="is-filterable full-checkbox"
          value="${option.value.toString()}"
          ?checked="${checkedValue}"
          ?isEllipsis="${true}"
          @change="${e => this.handleCheckboxChange(option, e)}"
          ><div class="d-flex flex-row align-items-center">
            ${this.renderIcon(option)}
            <gz-tooltip tooltipText="${option.label}" isEllipsis
              ><gz-text class="label" fontSize="14"
                >${this.decoratedTextOnSearch(searchValue, htmlEncode(option.label))}</gz-text
              ></gz-tooltip
            >
          </div>
        </gz-checkbox>
      </div>
    `;
  }

  private renderIcon(option: ComplexSuggestedItem): TemplateResult | undefined {
    if (!option.icon) {
      return;
    }

    const gzIcon = new GzIconLabelWebComponent();
    gzIcon.setCustomIconSet(this.iconSetInstance);
    gzIcon.size = FontSize.ICON_SIZE_20;
    gzIcon.icon = option.icon;
    gzIcon.classList.add('icon');
    gzIcon.iconColor = 'var(--chart-300-icon-buttons)';

    return html`${gzIcon}`;
  }

  setEntityIconSet(iconSet: TemplateResult): void {
    this.iconSetInstance = iconSet;
  }

  private decoratedTextOnSearch(searchValue: string, text: string) {
    return unsafeHTML(`${emphasizeString(searchValue, text)}`);
  }

  public handleCheckboxChange(option: ComplexSuggestedItem, e: CustomEvent) {
    this.updateDisplayedOptionsState(option, e.detail.checked);
    this.emitListItemToggledEvent(option, e.detail.checked);
  }

  /**
   * Uncheck all pinned items
   */
  public clearPinnedItems() {
    this.pinnedItems = this.pinnedItems.map(item => {
      return {
        ...item,
        checked: false,
      };
    });
  }

  protected hasSearchInput(): boolean {
    return true;
  }

  protected async handleOptionsSearch(e: CustomEvent) {
    const searchString = e.detail.sourceEvent.trim();

    // Multiple changed events arrives when typing, we should continue only if new search string is different than the last one
    // TODO @ilazar: This logic and oldSearchString property should be removed when
    //  https://bitdefender-bsg.atlassian.net/browse/FED-2853 is done
    if (this.oldSearchString === searchString) {
      return;
    }

    this.oldSearchString = searchString;

    const event = new CustomEvent(ComplexEnumListEvents.COMPLEX_SEARCH_CHANGE, {
      detail: {
        searchString,
      },
      bubbles: true,
      cancelable: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  public getCheckedPinnedItems(): ComplexPinnedListItem[] {
    return this.pinnedItems.filter(selectedOption => {
      return selectedOption.checked;
    });
  }

  /**
   * Returns all pinned items (could be checked or unchecked)
   */
  public getAllPinnedItems(): ComplexPinnedListItem[] {
    return this.pinnedItems;
  }

  /**
   * Two use cases:
   *  - If search is performed, the suggested items will contain the list with all suggested items
   *  - If no search, the suggested items will not contain the items that are in pinned items, because we want to be
   *    rendered as pinned items only (on top of the list)
   *
   * @param suggestedItems
   */
  public getSuggestedItemsBasedOnSearchState(suggestedItems) {
    if (!this.noSearchPerformed()) {
      return suggestedItems;
    }

    return suggestedItems.filter(item => {
      const matchItem = this.pinnedItems.filter(pinnedOption => {
        return pinnedOption.value === item.value && pinnedOption.checked;
      });
      return matchItem.length === 0;
    });
  }

  private noSearchPerformed() {
    if (!this.searchInput) {
      return true;
    }

    return this.getSearchInputValue() === '';
  }

  public emitConfirmSelectionEvent() {
    const checkedOptions = this.getCheckedPinnedItems();
    const event = new CustomEvent(ComplexEnumListEvents.COMPLEX_SELECTION_CHANGE, {
      detail: {
        selectedValues: checkedOptions.map(option => {
          const item = {};
          if (!this.ignoredFieldsForFilters.includes('label')) {
            item['label'] = option.label;
          }
          if (!this.ignoredFieldsForFilters.includes('value')) {
            item['value'] = option.value;
          }
          if (!this.ignoredFieldsForFilters.includes('type') && (option.type !== null || option.type !== 'undefined')) {
            item['type'] = option.type;
          }
          return item;
        }),
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  /**
   * When confirm button is pressed, we must update the suggested items: we should exclude pinned items from the list,
   * of suggested items, because when the component is opened again, the item will be displayed at the top of the
   * list as a pinned item
   */
  public updateSuggestedItemsWhenConfirmSelection() {
    const checkedPinnedItems = this.getCheckedPinnedItems();

    this.suggestedItems = this.suggestedItems.filter(item => {
      const matchItem = checkedPinnedItems.filter(pinnedOption => {
        return pinnedOption.value === item.value && pinnedOption.checked;
      });
      return matchItem.length === 0;
    });
  }

  protected emitListItemToggledEvent(option: ComplexSuggestedItem, checked: boolean) {
    this.dispatchEvent(
      new CustomEvent(ComplexEnumListEvents.COMPLEX_OPTION_CHANGED, {
        detail: {
          option: option,
          checked: checked,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private emitSuggestedItemsResultsEvent(currentSuggestedItems: ComplexSuggestedItem[]) {
    const event = new CustomEvent(ComplexEnumListEvents.COMPLEX_OPTIONS_FILTERED, {
      detail: {
        resultsFound: !isEmpty(currentSuggestedItems),
        noSearchPerformed: this.noSearchPerformed(),
        hasErrors: this.isError,
      },
      composed: true,
    });
    this.dispatchEvent(event);
  }

  protected updateDisplayedOptionsState(option: ComplexSuggestedItem, checked: boolean) {
    if (this.noSearchPerformed()) {
      this.updateStateNoSearch(checked, option);
    } else {
      this.updateStateWhenSearchIsActive(checked, option);
    }
  }

  /**
   * When no active search, we should display pinned items too. Use cases:
   *  - If current item does NOT exist in pinned items, and it is CHECKED, then add it to the list of pinned items
   *  - If current item exists in pinned items, then we must update it: CHECKED or UNCHECKED
   *
   * @param checked
   * @param option
   * @protected
   */
  protected updateStateNoSearch(checked, option) {
    const itemExistsInPinnedItemsList = this.checkItemExistsInPinnedItemsList(option);

    if (checked && !itemExistsInPinnedItemsList) {
      this.pinnedItems.push({
        label: option.label,
        value: option.value,
        type: option.type,
        icon: option.icon,
        checked,
      });
      this.requestUpdate('pinnedItems');
    } else if (itemExistsInPinnedItemsList) {
      // Mark as checked/unchecked, do not remove it from the list

      this.pinnedItems = this.pinnedItems.map(item => {
        if (item.value === option.value) {
          return {
            ...item,
            checked,
          };
        }

        return item;
      });

      this.requestUpdate('pinnedItems');
    }
  }

  /**
   * When active search, we should NOT display pinned items. Use cases:
   *  - If current item does NOT exist in pinned items, and it is CHECKED, then add it to the list of pinned items
   *  - If current item exists in pinned items, and it is UNCHECKED, then remove it from the list of pinned items
   * @param checked
   * @param option
   * @protected
   */
  protected updateStateWhenSearchIsActive(checked, option) {
    const itemExistsInSelectedOptionsList = this.checkItemExistsInPinnedItemsList(option);

    if (checked && !itemExistsInSelectedOptionsList) {
      this.pinnedItems.push({
        label: option.label,
        value: option.value,
        type: option.type,
        icon: option.icon,
        checked,
      });
    } else if (!checked && itemExistsInSelectedOptionsList) {
      // We will remove it from the list with all pinned items

      this.pinnedItems = this.pinnedItems.filter(item => {
        return item.value !== option.value;
      });
    }
  }

  public resetScrollLocation() {
    this.listOptions.scrollTop = 0;
    this.scrolledLocation = 0;
  }

  public focusInput() {
    this.updateComplete.then(() => {
      this.searchInput.inputFocus();
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-complex-enum-list': GzComplexEnumListWebComponent;
  }
}
